import React, { createContext, useContext, useEffect, useState } from "react";
import { configureAmplify, isHostedUIConfigured } from "../utils/amplifyConfig";
import {
  signIn,
  signOut,
  getCurrentUser,
  signUp,
  confirmSignUp,
  signInWithRedirect,
  fetchAuthSession,
  fetchUserAttributes,
  resetPassword as cognitoResetPassword,
  confirmResetPassword as cognitoConfirmResetPassword,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

type User = {
  username: string;
  userId: string;
  email?: string;
} | null;

interface AuthContextValue {
  user: User;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resetPassword: (username: string) => Promise<void>;
  confirmResetPassword: (username: string, code: string, newPassword: string) => Promise<void>;
  startHostedLogin: () => Promise<void>;
  hostedUIEnabled: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function getUserWithEmail(): Promise<User> {
  const u = await getCurrentUser();
  let email: string | undefined;
  try {
    const attrs = await fetchUserAttributes();
    email = attrs.email ?? attrs["email"] ?? undefined;
  } catch {
    // email remains undefined; username may be email if using email sign-in
  }
  return {
    username: u.username,
    userId: u.userId,
    email: email ?? (u.username.includes("@") ? u.username : undefined),
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    configureAmplify();
    (async () => {
      let provisional = false;
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken as { payload?: { sub?: string; exp?: number; email?: string } } | undefined;
        const sub = idToken?.payload?.sub;
        const expSec = idToken?.payload?.exp;
        const emailFromToken = idToken?.payload?.email;
        const idTokenValid = expSec ? expSec * 1000 > Date.now() + 5000 : false;
        if (sub && idTokenValid) {
          setUser({ username: sub, userId: sub, email: emailFromToken });
          provisional = true;
        }
      } catch {
        // ignore
      }
      try {
        const u = await getUserWithEmail();
        setUser(u);
      } catch {
        if (!provisional) setUser(null);
      } finally {
        setLoading(false);
      }
    })();
    const unsub = Hub.listen("auth", async ({ payload }) => {
      if (payload.event === "signedIn") {
        try {
          const u = await getUserWithEmail();
          setUser(u);
        } catch {
          // ignore
        }
      } else if (payload.event === "signedOut") {
        setUser(null);
      }
    });
    return () => unsub();
  }, []);

  const handleSignIn = async (email: string, password: string) => {
    try {
      const existing = await getCurrentUser().catch(() => null);
      if (existing?.userId) {
        const u = await getUserWithEmail();
        setUser(u);
        return;
      }
      await signIn({ username: email, password });
      const u = await getUserWithEmail();
      setUser(u);
    } catch (err) {
      throw err;
    }
  };

  const startHostedLogin = async () => {
    if (!isHostedUIConfigured()) throw new Error("Hosted UI is not configured");
    await signInWithRedirect({});
  };

  const handleSignOut = async () => {
    await signOut();
    setUser(null);
  };

  const handleSignUp = async (email: string, password: string) => {
    await signUp({
      username: email,
      password,
      options: { userAttributes: { email } },
    });
  };

  const handleConfirmSignUp = async (email: string, code: string) => {
    await confirmSignUp({ username: email, confirmationCode: code });
  };

  const handleResetPassword = async (username: string) => {
    await cognitoResetPassword({ username });
  };

  const handleConfirmResetPassword = async (
    username: string,
    code: string,
    newPassword: string
  ) => {
    await cognitoConfirmResetPassword({ username, confirmationCode: code, newPassword });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn: handleSignIn,
        signOut: handleSignOut,
        signUp: handleSignUp,
        confirmSignUp: handleConfirmSignUp,
        resetPassword: handleResetPassword,
        confirmResetPassword: handleConfirmResetPassword,
        startHostedLogin,
        hostedUIEnabled: isHostedUIConfigured(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
